import { supabase } from './supabaseClient';
import { format } from 'date-fns';

export interface AvailabilitySlot {
  id: string;
  available_date: string;
  available_from_time: string;
  available_to_time: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get all available dates from the availability table
 * Returns array of date strings in YYYY-MM-DD format
 */
export async function getAvailableDates(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('availability')
      .select('available_date')
      .gte('available_date', new Date().toISOString().split('T')[0])
      .order('available_date', { ascending: true });

    if (error) {
      console.error('Error fetching available dates:', error);
      return [];
    }

    // Get unique dates
    const uniqueDates = [...new Set(data?.map(item => item.available_date) || [])];
    return uniqueDates;
  } catch (err) {
    console.error('Error in getAvailableDates:', err);
    return [];
  }
}

/**
 * Check if a specific date is available
 */
export function isDateAvailable(date: Date, availableDates: string[]): boolean {
  const dateStr = format(date, 'yyyy-MM-dd');
  return availableDates.includes(dateStr);
}

/**
 * Get available time slots for a specific date
 * Returns array of time slots in HH:MM format
 */
export async function getAvailableTimesForDate(date: Date): Promise<string[]> {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    const { data, error } = await supabase
      .from('availability')
      .select('available_from_time, available_to_time, "30min_set"')
      .eq('available_date', dateStr)
      .order('available_from_time', { ascending: true });

    if (error) {
      console.error('Error fetching available times:', error);
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Generate time slots for each availability period
    const allTimeSlots: string[] = [];
    
    data.forEach(slot => {
      // Check if this slot uses 30-minute intervals (default to true for backward compatibility)
      const use30MinIntervals = slot['30min_set'] === 'TRUE';
      const timeSlots = generateTimeSlots(slot.available_from_time, slot.available_to_time, use30MinIntervals);
      allTimeSlots.push(...timeSlots);
    });

    // Remove duplicates and sort
    const uniqueTimeSlots = [...new Set(allTimeSlots)].sort();
    return uniqueTimeSlots;
  } catch (err) {
    console.error('Error in getAvailableTimesForDate:', err);
    return [];
  }
}

/**
 * Generate 30-minute time slots between from and to times
 * Includes both start and end times in the available slots
 */
function generateTimeSlots(fromTime: string, toTime: string, use30MinIntervals: boolean = true): string[] {
  const slots: string[] = [];
  
  // Parse time strings (HH:MM format)
  const [fromHour, fromMinute] = fromTime.split(':').map(Number);
  const [toHour, toMinute] = toTime.split(':').map(Number);
  
  // Convert to minutes for easier calculation
  const fromMinutes = fromHour * 60 + fromMinute;
  const toMinutes = toHour * 60 + toMinute;
  
  // Determine interval based on setting (30 minutes or 60 minutes)
  const interval = use30MinIntervals ? 30 : 60;
  
  // Generate slots with specified interval including the end time
  for (let minutes = fromMinutes; minutes <= toMinutes; minutes += interval) {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    slots.push(timeSlot);
  }
  
  return slots;
}

/**
 * Organize time slots by period (morning, afternoon, evening)
 */
export function organizeTimesByPeriod(times: string[]) {
  const morning: string[] = [];
  const afternoon: string[] = [];
  const evening: string[] = [];

  times.forEach(time => {
    const hour = parseInt(time.split(':')[0]);
    if (hour < 12) {
      morning.push(time);
    } else if (hour < 17) {
      afternoon.push(time);
    } else {
      evening.push(time);
    }
  });

  return { morning, afternoon, evening };
}